// Libraries
import React, { useEffect, useRef, useState } from 'react';
import _isEqual from 'lodash/isEqual';
import _cloneDeep from 'lodash/cloneDeep';
import { Dropdown, Form } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
// actions
import { Search } from '../../../actions/search';
// Custom Hooks
import usePrevious from '../../../hooks/helpers/usePrevious';
// Constants
import { LEGEND_STATUSES } from '../../../constants/statuses';


export const StatusSelector = () => {
  const {
    requestsInProgress: { count: loading },
    search: { searchParams: stateSearchParams },
    user: { info: { permissions: userPermissions } },
  } = useSelector(state => state);
  const dispatch = useDispatch();

  const prevSearchParams = usePrevious(stateSearchParams);
  const [statusLegend, setStatusLegend] = useState([]);
  const [searchParams, setSearchParams] = useState(stateSearchParams || {});
  const [dropDownShow, setDropDownShow] = useState(false);
  const [menuSelectedStatus, setMenuSelectedStatus] = useState(null);
  const menuRef = useRef(null);

  const MAX_AVATARS = 3;

  const handleSearch = (params) => {
    const newParams = _cloneDeep(params);
    dispatch(Search({ ...newParams }, true));
  };

  const saveParam = () => {
    const statusIds = statusLegend.map(status => status.id);
    setSearchParams(prevParams => ({ ...prevParams, status: statusIds }));
    handleSearch({ ...searchParams, status: statusIds });
  };

  const clearParam = () => {
    const updatedParams = { ...searchParams };
    delete updatedParams.status;
    setSearchParams(updatedParams);
    handleSearch(updatedParams);
  };

  const allowedStatuses = Object.entries(LEGEND_STATUSES)
    .filter(
      ([, status]) => !status.permissions
        || status.permissions
          .some(perm => userPermissions?.patientViewPermissions?.includes(perm)),
    );

  const handleSelect = (event, status) => {
    if (loading) return;

    // Update the current selected item to reselect after loading
    setMenuSelectedStatus(event?.currentTarget);

    if (status.id !== LEGEND_STATUSES.ANY.id) {
      // Stop the menu from closing
      setDropDownShow(true);

      // If already selected, filter out the unchecked status
      const newSelected = statusLegend.includes(status)
        ? statusLegend.filter(s => s !== status)
        : [...statusLegend, status];

      setStatusLegend(newSelected);
    } else {
      setStatusLegend([]);
    }
  };

  const handleMenuBlur = (event) => {
    const menuFocus = event.relatedTarget
      && menuRef.current
      && menuRef.current.contains(event.relatedTarget);
    if (!menuFocus) {
      setDropDownShow(false);
    }
  };

  const renderItem = ({ color, id, name }) => (
    <div className="d-flex-center" key={`status-item-${id === '*' ? 'all' : id}`}>
      <span className={`avatar-icon bg-ccm-${color} text-white rounded-circle mr-2`}>
        {id}
      </span>
      <span className="text-left status-label" data-test="statusSelector_dropDown">{name}</span>
    </div>
  );

  const statusLabelText = (statuses) => {
    if (statuses.length === 1) return statuses[0].name;
    if (statuses.length > MAX_AVATARS) {
      return `+${statuses.length - MAX_AVATARS}`;
    }
    return '';
  };

  const renderMultipleStatus = (statuses) => {
    let avatarCount = 0;
    const orderedStatuses = Object.entries(LEGEND_STATUSES)
      .filter(([, ls]) => statuses.some(status => status.id === ls.id))
      .map(([, ls]) => ls);
    const statusAvatars = orderedStatuses
      .map((status) => {
        if (avatarCount < MAX_AVATARS) {
          avatarCount++;
          return (
            <span
              className={`avatar-icon bg-ccm-${status.color} text-white rounded-circle mr-2`}
              key={`avatar-${status.id}`}
            >
              {status.id}
            </span>
          );
        }
        return '';
      });
    return (
      <div className="d-flex-center">
        {statusAvatars}
        <span
          className="text-left status-label"
          data-test="statusSelector_dropDown"
        >
          {statusLabelText(statuses)}
        </span>
      </div>
    );
  };

  const renderMenuButton = statuses => ((statuses === null || statuses.length === 0)
    ? renderMultipleStatus([LEGEND_STATUSES.ANY])
    : renderMultipleStatus(statuses));

  const renderItems = () => allowedStatuses
    .map(([, status], statusIndex) => (
      <Dropdown.Item
        key={`legend-status-item-${statusIndex}`}
        onClick={event => handleSelect(event, status)}
        onBlur={handleMenuBlur}
        as="button"
        className="d-flex my-1 border-0 px-3"
        data-test="statusSelector_dropDownItem"
        disabled={loading}
      >
        <Form.Check
          type="checkbox"
          style={{
            paddingTop: 2,
            paddingRight: 10,
            visibility: status === LEGEND_STATUSES.ANY ? 'hidden' : 'visible',
          }}
          data-test={`checkbox-${statusIndex}`}
          checked={statusLegend.includes(status)}
          onChange={event => handleSelect(event, status)}
          onBlur={handleMenuBlur}
          disabled={loading}
        />
        {renderItem(status)}
      </Dropdown.Item>
    ));

  useEffect(() => {
    if (!_isEqual(searchParams, prevSearchParams)) {
      setSearchParams(searchParams);
    }
  }, [searchParams]);

  useEffect(() => {
    setSearchParams(stateSearchParams);

    if (stateSearchParams && stateSearchParams.status) {
      const currentStatus = allowedStatuses
        .filter(([, allowedStatus]) => (stateSearchParams.status instanceof Array
          ? stateSearchParams.status.some(searchStatus => allowedStatus.id === searchStatus)
          : allowedStatus.id === stateSearchParams.status))
        .map(([, allowedStatus]) => allowedStatus);

      // Filter out existing status
      const newStatus = currentStatus.filter(cs => !statusLegend.some(sl => sl.id === cs.id));
      // Update status if there are new items
      if (newStatus.length > 0) {
        setStatusLegend([...statusLegend, ...newStatus]);
      }
    }
  }, [stateSearchParams]);

  useEffect(() => {
    if (statusLegend.length > 0) {
      saveParam();
    } else {
      clearParam();
    }
  }, [statusLegend]);

  useEffect(() => {
    if (!loading && menuSelectedStatus) {
      menuSelectedStatus.focus();
      setMenuSelectedStatus(null);
    }
  }, [loading]);

  return (
    <div className="d-flex align-items-center select-status">
      <Dropdown show={dropDownShow}>
        <Dropdown.Toggle
          variant="light"
          onClick={() => setDropDownShow(!dropDownShow)}
          onBlur={handleMenuBlur}
        >
          {renderMenuButton(statusLegend)}
        </Dropdown.Toggle>
        <Dropdown.Menu
          ref={menuRef}
          onBlur={handleMenuBlur}
        >
          {renderItems()}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default StatusSelector;
